import React, { useEffect, useState } from "react";
import {
    Avatar,
    Button,
    Card,
    Empty,
    Input,
    Popconfirm,
    Row,
    Col,
    Select,
    Space,
    Tag,
    Tooltip,
    Typography,
    DatePicker
} from "antd";
import dayjs from "dayjs";
import {
    CheckOutlined,
    CloseOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import { message } from "antd";
import { reviewCommentsAndActionsService } from "../../../services/reviewCommentsAndActionsService";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useQuery } from "@tanstack/react-query";
import DataState from "../../../components/DataState/DataState";

const TypographyText = Typography.Text;









const formatDate = (dateValue) => {
    if (!dateValue) return "";

    const date = new Date(dateValue);

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();

    return `${month}-${day}-${year}`;
};

const priorityOptions = [
    { value: "Critical", label: "🔴 Critical" },
    { value: "High", label: "🔴 High" },
    { value: "Medium", label: "🟡 Medium" },
    { value: "Low", label: "🟢 Low" },
];


const statusOptions = [
    { value: "Open", label: "Open" },
    { value: "In Progress", label: "In Progress" },
    { value: "Resolved", label: "Resolved" },
    { value: "Closed", label: "Closed" },
];

const getInitials = (name) => {
    if (!name) return "-";

    return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
};

const getPriorityColor = (priority) => {
    if (priority === "Critical") return "#ff0000";
    if (priority === "High") return "#ff7a45";
    if (priority === "Medium") return "#FABB02";
    return "#52c41a";
};

const getStatusColor = (status) => {
    if (status === "Resolved" || status === "Closed") return "#52c41a";
    if (status === "In Progress") return "#1677ff";
    return "#FABB02";
};

const CommentCard = ({
    comment,
    readOnly = false,
    editingCommentId,
    editingCommentText = "",
    onEdit,
    onEditTextChange,
    onSaveEdit,
    onCancelEdit,
    onDelete,
}) => {
    const avatarColorMap = {
        Risk: "#ff7a45",
        Milestone: "#fa8231",
        General: "#1677ff",
    };

    const typeColorMap = {
        Risk: "error",
        Milestone: "warning",
        General: "blue",
    };

    const isEditing = editingCommentId === comment.commentId;

    return (
        <div
            style={{
                paddingBottom: 16,
                marginBottom: 16,
                borderBottom: "1px solid #f0f0f0",
                display: "flex",
                gap: 12,
            }}
        >
            <Avatar
                style={{
                    background: "#1677ff",
                    flexShrink: 0,
                    fontSize: 12,
                    fontWeight: 600,
                }}
            >
                {getInitials(comment.createdBy)}
            </Avatar>

            <div style={{ flex: 1 }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 4,
                        justifyContent: "space-between",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Typography.Text strong style={{ fontSize: 14 }}>
                            {comment.createdBy}
                        </Typography.Text>

                        {/* {comment.type && (
              <Tag
                color={typeColorMap[comment.type] || "blue"}
                style={{ fontSize: 11, marginLeft: 4 }}
              >
                {comment.type}
              </Tag>
            )} */}

                        {comment.role && (
                            <Typography.Text
                                type="secondary"
                                style={{ fontSize: 12, marginLeft: 4 }}
                            >
                                {comment.role}
                            </Typography.Text>
                        )}
                    </div>

                    {!readOnly && (
                        <Space size="small">
                            <Button
                                type="text"
                                size="small"
                                icon={<EditOutlinedIcon style={{ fontSize: 15 }} />}
                                onClick={() => onEdit?.(comment.commentId)}
                                title="Edit"
                            />

                            <Popconfirm
                                title="Delete Comment"
                                description="Are you sure you want to delete this comment?"
                                onConfirm={() => onDelete?.(comment.commentId)}
                                okText="Delete"
                                cancelText="Cancel"
                                okButtonProps={{ danger: true }}
                            >
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    danger
                                    title="Delete"
                                />
                            </Popconfirm>
                        </Space>
                    )}
                </div>

                {!readOnly && isEditing ? (
                    <div style={{ marginBottom: 8 }}>
                        <Input.TextArea
                            value={editingCommentText}
                            onChange={(e) => onEditTextChange?.(e.target.value)}
                            rows={3}
                            style={{ marginBottom: 8, borderRadius: 6 }}
                            autoFocus
                        />

                        <Space size="small">
                            <Button
                                size="small"
                                type="primary"
                                icon={<CheckOutlined />}
                                onClick={onSaveEdit}
                            >
                                Save
                            </Button>

                            <Button
                                size="small"
                                icon={<CloseOutlined />}
                                onClick={onCancelEdit}
                            >
                                Cancel
                            </Button>
                        </Space>
                    </div>
                ) : (
                    <Typography.Text
                        style={{
                            color: "#595959",
                            fontSize: 13,
                            lineHeight: 1.5,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            display: "block",
                            marginBottom: 8,
                        }}
                    >
                        {comment.comment}
                    </Typography.Text>
                )}

                {comment.createdAt && (
                    <Typography.Text
                        type="secondary"
                        style={{
                            fontSize: 12,
                            display: "block",
                        }}
                    >
                        {formatDate(comment.createdAt)}
                    </Typography.Text>
                )}
            </div>
        </div>
    );
};

const ReviewCommentsAndActionItemsView = ({
    projectId,
    comments = [],
    actions = [],
    readOnly = false,

    editingCommentId,
    editingCommentText,
    onEditComment,
    onEditCommentTextChange,
    onSaveCommentEdit,
    onCancelCommentEdit,
    onDeleteComment,

    editingRowId,
    editingRowData = {},
    onEditAction,
    onUpdateEditingRowField,
    onSaveActionEdit,
    onCancelActionEdit,
    onDeleteAction,
}) => {

    // Read-only views load their own comments and actions. The query is "loading" on the
    // first render of a new project and during a retry; gcTime 0 means a remount never
    // reuses previously loaded rows.
    const reviewDataQuery = useQuery({
        queryKey: ["project-review", "comments-actions", projectId],
        queryFn: async () => {
            const [commentsResponse, actionsResponse] =
                await Promise.all([
                    reviewCommentsAndActionsService.getComments(projectId),
                    reviewCommentsAndActionsService.getActions(projectId),
                ]);

            return {
                comments: commentsResponse || [],
                actions: actionsResponse || [],
            };
        },
        enabled: readOnly && Boolean(projectId),
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: "always",
        retry: false,
    });

    const loadedComments = reviewDataQuery.data?.comments ?? [];
    const loadedActions = reviewDataQuery.data?.actions ?? [];

    const loadStatus =
        !readOnly || !projectId
            ? "ready"
            : reviewDataQuery.isLoading || (reviewDataQuery.isError && reviewDataQuery.isFetching)
                ? "loading"
                : reviewDataQuery.isError
                    ? "error"
                    : "ready";


    const displayComments = readOnly
        ? loadedComments
        : comments;

    const displayActions = readOnly
        ? loadedActions
        : actions;



    const openActionCount = displayActions.filter(
        (action) => action.status !== "Resolved" && action.status !== "Closed"
    ).length;



    return (
        <DataState
            status={loadStatus}
            errorTitle="Couldn't load review comments and actions"
            error={reviewDataQuery.error?.message || "Failed to load review comments and actions"}
            onRetry={() => reviewDataQuery.refetch()}
            skeletonRows={6}
        >
        <>
            {/* Comments List Row */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24}>
                    <Card
                        style={{
                            borderRadius: 14,
                            border: "1px solid #f0f0f0",
                        }}
                        styles={{ body: { padding: 16 } }}
                        title={
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontWeight: 600, fontSize: 14 }}>
                                    Review Comments
                                </span>

                                <Tag
                                    color="blue"
                                    style={{
                                        borderRadius: 12,
                                        padding: "0 8px",
                                        fontSize: 12,
                                        fontWeight: 600,
                                    }}
                                >
                                    {displayComments.length}
                                </Tag>
                            </div>
                        }
                    >
                        <div style={{ maxHeight: 400, overflowY: "auto" }}>
                            {displayComments.length === 0 ? (
                                <Empty description="No comments yet" style={{ marginTop: 32 }} />
                            ) : (
                                <div>
                                    {displayComments.map((comment) => (
                                        <CommentCard
                                            key={comment.commentId}
                                            comment={comment}
                                            readOnly={readOnly}
                                            editingCommentId={editingCommentId}
                                            editingCommentText={editingCommentText}
                                            onEdit={onEditComment}
                                            onEditTextChange={onEditCommentTextChange}
                                            onSaveEdit={onSaveCommentEdit}
                                            onCancelEdit={onCancelCommentEdit}
                                            onDelete={onDeleteComment}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Actions Table Row */}
            <Row gutter={[16, 16]}>
                <Col xs={24}>
                    <Card
                        style={{
                            borderRadius: 14,
                            border: "1px solid #f0f0f0",
                        }}
                        styles={{ body: { padding: 16 } }}
                        title={
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontWeight: 600, fontSize: 14 }}>
                                    Action Items
                                </span>

                                <Tag
                                    color="orange"
                                    style={{
                                        borderRadius: 12,
                                        padding: "0 8px",
                                        fontSize: 12,
                                        fontWeight: 600,
                                    }}
                                >
                                    {openActionCount} open
                                </Tag>
                            </div>
                        }
                    >
                        <div >
                            {displayActions.length === 0 ? (
                                <Empty
                                    description="No action items yet"
                                    style={{ marginTop: 32 }}
                                />
                            ) : (
                                <div style={{ fontSize: 13, minWidth: 980 }}>
                                    {/* Table Header */}
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: readOnly
                                                ? "2fr 1.2fr 1fr 1fr 1fr 0.8fr"
                                                : "2fr 1.2fr 1fr 1fr 1fr 0.8fr",
                                            gap: 12,
                                            minWidth: readOnly ? 980 : 980,
                                            paddingBottom: 12,
                                            marginBottom: 12,
                                            borderBottom: "2px solid #f0f0f0",
                                            fontWeight: 600,
                                            fontSize: 12,
                                            color: "#595959",
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        <div>ACTION ITEM</div>
                                        <div>OWNER</div>
                                        <div>DUE DATE</div>
                                        <div>PRIORITY</div>
                                        <div>STATUS</div>
                                        {!readOnly && <div>ACTIONS</div>}
                                    </div>

                                    {/* Table Rows */}
                                    {displayActions.map((action) => {
                                        const isRowEditing = editingRowId === action.actionId;

                                        return (
                                            <div
                                                key={action.actionId}
                                                style={{
                                                    display: "grid",
                                                    gridTemplateColumns: readOnly
                                                        ? "2fr 1.2fr 1fr 1fr 1fr 0.8fr"
                                                        : "2fr 1.2fr 1fr 1fr 1fr 0.8fr",
                                                    gap: 12,
                                                    minWidth: readOnly ? 980 : 980,
                                                    paddingBottom: 12,
                                                    marginBottom: 12,
                                                    borderBottom: "1px solid #f0f0f0",
                                                    alignItems: "center",
                                                }}
                                            >
                                                {/* Action Item */}
                                                <div style={{ padding: "4px 0" }}>
                                                    {!readOnly && isRowEditing ? (
                                                        <Input.TextArea
                                                            value={editingRowData.description}
                                                            onChange={(e) =>
                                                                onUpdateEditingRowField?.(
                                                                    "description",
                                                                    e.target.value
                                                                )
                                                            }
                                                            rows={2}
                                                            style={{ fontSize: 12, width: "100%" }}
                                                        />
                                                    ) : (
                                                        <Typography.Text
                                                            style={{ fontSize: 13, color: "#262626" }}
                                                        >
                                                            {action.actionItem}
                                                        </Typography.Text>
                                                    )}
                                                </div>

                                                {/* Owner */}
                                                <div style={{ padding: "4px 0" }}>
                                                    {!readOnly && isRowEditing ? (
                                                        <Input
                                                            value={editingRowData.owner}
                                                            onChange={(e) =>
                                                                onUpdateEditingRowField?.("owner", e.target.value)
                                                            }
                                                            placeholder="Enter owner"
                                                            style={{ width: "100%" }}
                                                        />
                                                    ) : (
                                                        <Typography.Text
                                                            style={{ fontSize: 12, color: "#262626" }}
                                                        >
                                                            {action.owner || "NA"}
                                                        </Typography.Text>
                                                    )}
                                                </div>

                                                {/* Due Date */}
                                                <div style={{ padding: "4px 0" }}>
                                                    {!readOnly && isRowEditing ? (
                                                        <DatePicker
                                                            value={
                                                                editingRowData.dueDate
                                                                    ? dayjs(editingRowData.dueDate)
                                                                    : null
                                                            }
                                                            format="MM-DD-YYYY"
                                                            style={{ width: "100%" }}
                                                            onChange={(date) =>
                                                                onUpdateEditingRowField?.(
                                                                    "dueDate",
                                                                    date ? date.format("YYYY-MM-DD") : ""
                                                                )
                                                            }
                                                        />
                                                    ) : (
                                                        <Typography.Text
                                                            style={{ fontSize: 12, color: "#262626" }}
                                                        >
                                                            {formatDate(action.dueDate)}
                                                        </Typography.Text>
                                                    )}
                                                </div>

                                                {/* Priority */}
                                                <div style={{ padding: "4px 0" }}>
                                                    {!readOnly && isRowEditing ? (
                                                        <Select
                                                            value={editingRowData.priority}
                                                            onChange={(value) =>
                                                                onUpdateEditingRowField?.("priority", value)
                                                            }
                                                            options={priorityOptions}
                                                            style={{ width: "100%" }}
                                                        />
                                                    ) : (
                                                        <Tag
                                                            color={getPriorityColor(action.priority || "Low")}
                                                            style={{ fontSize: 11, fontWeight: 600 }}
                                                        >
                                                            {action.priority || "NA"}
                                                        </Tag>
                                                    )}
                                                </div>

                                                {/* Status */}
                                                <div style={{ padding: "4px 0" }}>
                                                    {!readOnly && isRowEditing ? (
                                                        <Select
                                                            value={editingRowData.status}
                                                            onChange={(value) =>
                                                                onUpdateEditingRowField?.("status", value)
                                                            }
                                                            options={statusOptions}
                                                            style={{ width: "100%" }}
                                                        />
                                                    ) : (
                                                        <Typography.Link
                                                            style={{
                                                                fontSize: 12,
                                                                color: getStatusColor(action.status || "Low"),
                                                            }}
                                                        >
                                                            {action.status || "NA"}
                                                        </Typography.Link>
                                                    )}
                                                </div>

                                                {/* Actions */}
                                                {!readOnly && (
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            gap: 6,
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        {isRowEditing ? (
                                                            <>
                                                                <Tooltip title="Save">
                                                                    <Button
                                                                        type="primary"
                                                                        size="small"
                                                                        icon={<CheckOutlined />}
                                                                        onClick={onSaveActionEdit}
                                                                    />
                                                                </Tooltip>

                                                                <Tooltip title="Cancel">
                                                                    <Button
                                                                        size="small"
                                                                        icon={<CloseOutlined />}
                                                                        onClick={onCancelActionEdit}
                                                                    />
                                                                </Tooltip>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Tooltip title="Edit">
                                                                    <Button
                                                                        type="text"
                                                                        size="small"
                                                                        icon={<EditOutlinedIcon style={{ fontSize: 15 }} />}
                                                                        onClick={() => onEditAction?.(action.actionId)}
                                                                    />
                                                                </Tooltip>

                                                                <Popconfirm
                                                                    title="Delete Action"
                                                                    description="Are you sure you want to delete this action?"
                                                                    onConfirm={() => onDeleteAction?.(action.actionId)}
                                                                    okText="Delete"
                                                                    cancelText="Cancel"
                                                                    okButtonProps={{ danger: true }}
                                                                >
                                                                    <Button
                                                                        type="text"
                                                                        size="small"
                                                                        icon={<DeleteOutlined />}
                                                                        danger
                                                                    />
                                                                </Popconfirm>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </Card>
                </Col>
            </Row>
        </>
        </DataState>
    );
};

export default ReviewCommentsAndActionItemsView;